/**
 * Rasterize store / override logos to PNG and upload to S3 on Apply.
 * Preview keeps original URLs; Apply embeds CDN PNG URLs with ?v= cache bust.
 *
 * Remote logos (e.g. Prismic SVG) are fetched via same-origin `/email-theme-asset`
 * (see src/setupProxy.js) to avoid browser CORS.
 */

import { mergeLogoSettings } from "./logoTokens";
import { svgToPngBlob, uploadEmailThemeIcon } from "./uploadIcons";

/** Retina factor for email logo PNGs. */
const LOGO_PNG_SCALE = 2;

/**
 * Same-origin proxy path (CRA setupProxy in dev; host must mirror in prod).
 * @param {string} sourceUrl
 */
export function proxiedLogoFetchUrl(sourceUrl) {
    return `/email-theme-asset?url=${encodeURIComponent(sourceUrl)}`;
}

function parseSvgAspect(svgMarkup) {
    const markup = String(svgMarkup || "");
    const vb = markup.match(
        /viewBox=["']?\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/i,
    );
    if (vb) {
        const w = Number(vb[3]);
        const h = Number(vb[4]);
        if (w > 0 && h > 0) return h / w;
    }
    const wAttr = markup.match(/\bwidth="(\d+(?:\.\d+)?)"/i);
    const hAttr = markup.match(/\bheight="(\d+(?:\.\d+)?)"/i);
    if (wAttr && hAttr) {
        const w = Number(wAttr[1]);
        const h = Number(hAttr[1]);
        if (w > 0 && h > 0) return h / w;
    }
    return 1;
}

/**
 * @param {HTMLImageElement} img
 * @param {number} targetWidth CSS/display width in px (pre-retina)
 * @returns {Promise<Blob>}
 */
function imageElementToPngBlob(img, targetWidth) {
    const natW = img.naturalWidth || img.width || targetWidth;
    const natH = img.naturalHeight || img.height || targetWidth;
    const outW = Math.max(1, Math.round(targetWidth * LOGO_PNG_SCALE));
    const outH = Math.max(1, Math.round((natH / Math.max(1, natW)) * outW));

    return new Promise((resolve, reject) => {
        try {
            const canvas = document.createElement("canvas");
            canvas.width = outW;
            canvas.height = outH;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new Error("Canvas unavailable"));
                return;
            }
            ctx.drawImage(img, 0, 0, outW, outH);
            canvas.toBlob(blob => {
                if (!blob) {
                    reject(new Error("PNG encode failed"));
                    return;
                }
                resolve(blob);
            }, "image/png");
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * @param {string} sourceUrl
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(sourceUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () =>
            reject(new Error(`Could not load logo image: ${sourceUrl}`));
        img.src = sourceUrl;
    });
}

/**
 * @param {Response} resp
 * @param {boolean} looksSvg
 * @param {number} width
 */
async function responseToPngBlob(resp, looksSvg, width) {
    const contentType = (resp.headers.get("content-type") || "").toLowerCase();
    if (looksSvg || contentType.includes("svg")) {
        const svgText = await resp.text();
        const aspect = parseSvgAspect(svgText);
        const outW = width * LOGO_PNG_SCALE;
        const outH = Math.max(1, Math.round(outW * aspect));
        return svgToPngBlob(svgText, {
            scale: 1,
            width: outW,
            height: outH,
        });
    }
    const objectUrl = URL.createObjectURL(await resp.blob());
    try {
        const img = await loadImage(objectUrl);
        return await imageElementToPngBlob(img, width);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

/**
 * Fetch / load a remote logo and encode as PNG at ~2× display width.
 *
 * @param {string} sourceUrl
 * @param {number|string} displayWidth
 * @returns {Promise<Blob>}
 */
export async function logoUrlToPngBlob(sourceUrl, displayWidth) {
    const url = String(sourceUrl || "").trim();
    if (!url) {
        throw new Error("Missing logo URL");
    }
    const width = Math.max(
        1,
        Math.round(Number(String(displayWidth).match(/^(\d+)/)?.[1]) || 96),
    );
    const looksSvg = /\.svg(\?|#|$)/i.test(url);

    // 1) Same-origin proxy (avoids Prismic CDN CORS in local/dev).
    const proxyUrl = proxiedLogoFetchUrl(url);
    const proxyResp = await fetch(proxyUrl, { credentials: "same-origin" });
    if (proxyResp.ok) {
        return responseToPngBlob(proxyResp, looksSvg, width);
    }

    // 2) Direct CORS fetch (works when the CDN allows it).
    const directResp = await fetch(url, {
        mode: "cors",
        credentials: "omit",
    });
    if (!directResp.ok) {
        throw new Error(
            `Logo fetch failed (proxy ${proxyResp.status}, direct ${directResp.status})`,
        );
    }
    return responseToPngBlob(directResp, looksSvg, width);
}

/**
 * Resolve header/footer logo sources → PNG on CDN.
 * Empty customize fields fall back to store.logo / store.logo_small.
 * If a logo cannot be converted, keeps the original URL (Apply continues).
 *
 * @param {object | null | undefined} logoSettings
 * @param {object} ctx
 * @param {string} ctx.accountId
 * @param {string} ctx.storeId
 * @param {string} ctx.skinId
 * @param {string} [ctx.storeLogo]
 * @param {string} [ctx.storeLogoSmall]
 * @param {Function} ctx.generatePresignedUrl
 * @returns {Promise<ReturnType<typeof mergeLogoSettings>>}
 */
export async function resolveLogoSettingsForApply(logoSettings, ctx) {
    const {
        accountId,
        storeId,
        skinId,
        storeLogo = "",
        storeLogoSmall = "",
        generatePresignedUrl,
    } = ctx || {};
    if (!accountId || !storeId || !skinId || !generatePresignedUrl) {
        throw new Error("Missing upload context for email theme logos");
    }

    const merged = mergeLogoSettings(logoSettings);
    const headerSource =
        String(merged.headerUrl || "").trim() || String(storeLogo || "").trim();
    const footerSource =
        String(merged.footerUrl || "").trim() ||
        String(storeLogoSmall || "").trim();

    const cacheBust = Date.now();

    const uploadOne = async (sourceUrl, iconKey, displayWidth) => {
        if (!sourceUrl) return "";
        try {
            const blob = await logoUrlToPngBlob(sourceUrl, displayWidth);
            const cdnUrl = await uploadEmailThemeIcon({
                accountId,
                storeId,
                skinId,
                iconKey,
                blob,
                generatePresignedUrl,
            });
            return `${cdnUrl}?v=${cacheBust}`;
        } catch (err) {
            console.warn(
                `Email theme logo skipped (${iconKey}); keeping source URL.`,
                err,
            );
            return sourceUrl;
        }
    };

    const [headerUrl, footerUrl] = await Promise.all([
        uploadOne(headerSource, "logoHeader", merged.headerWidth),
        uploadOne(footerSource, "logoFooter", merged.footerWidth),
    ]);

    return {
        ...merged,
        headerUrl,
        footerUrl,
    };
}
